import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

export type AcquisitionSourcesFilters = {
  start_date: string;
  end_date: string;
  // v1 BE contract: single-value `state[eq]=<code>` query, or omitted for
  // "no filter" (equivalent to sending `state[eq]=all`).
  state?: string;
  // Server-side pagination — BE responds with `data.total/page/size/pages`
  // and a `data.items` window. Defaults are BE-driven (size=50) but we
  // pass them explicitly so the page count stays predictable as the user
  // navigates.
  page?: number;
  size?: number;
};

export type AcquisitionSourceRow = {
  key: string;
  utmCategorization: string;
  utmSource: string;
  totalFtds: number;
  redeemableWallet: number;
  depositAmount: number;
  withdrawalAmount: number;
  netRevenue: number;
  avgDeposit: number;
  avgWithdrawal: number;
  avgNetRevenue: number;
};

export type StateFilterOption = {
  value: string;
  label: string;
};

export type AcquisitionSourcesPagination = {
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type AcquisitionSourcesPayload = {
  rows: AcquisitionSourceRow[];
  stateOptions: StateFilterOption[];
  pagination: AcquisitionSourcesPagination;
};

type RawAcquisitionSourceRow = {
  categorization?: string | null;
  utm_source?: string | null;
  total_ftds?: number | string | null;
  redeemable_wallet?: number | string | null;
  deposit_amount?: number | string | null;
  withdrawal_amount?: number | string | null;
  net_revenue?: number | string | null;
  avg_deposit?: number | string | null;
  avg_withdrawal?: number | string | null;
  avg_net_revenue?: number | string | null;
};

type RawFilterOption = {
  value?: string | null;
  label?: string | null;
};

type RawFilterDef = {
  key?: string | null;
  options?: Array<string | RawFilterOption> | null;
};

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const slug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// UTM filters are applied client-side (not BE params in v1); state is
// `state[eq]=<code>` and the date range is forwarded as-is. `page`/`size`
// are only included when the caller sets them explicitly so the BE can
// fall back to its own defaults otherwise.
const buildParams = (filters: AcquisitionSourcesFilters) => ({
  start_date: filters.start_date,
  end_date: filters.end_date,
  ...(filters.state ? { "state[eq]": filters.state } : {}),
  ...(typeof filters.page === "number" ? { page: filters.page } : {}),
  ...(typeof filters.size === "number" ? { size: filters.size } : {}),
});

const mapRow = (
  raw: RawAcquisitionSourceRow,
  index: number,
): AcquisitionSourceRow => {
  const categorization = (raw?.categorization ?? "").trim();
  const source = (raw?.utm_source ?? "").trim();
  const key = `row-${index}-${slug(categorization) || "uncategorized"}-${slug(source) || "any"}`;

  return {
    key,
    utmCategorization: categorization,
    utmSource: source,
    totalFtds: coerceNumber(raw?.total_ftds),
    redeemableWallet: coerceNumber(raw?.redeemable_wallet),
    depositAmount: coerceNumber(raw?.deposit_amount),
    withdrawalAmount: coerceNumber(raw?.withdrawal_amount),
    netRevenue: coerceNumber(raw?.net_revenue),
    avgDeposit: coerceNumber(raw?.avg_deposit),
    avgWithdrawal: coerceNumber(raw?.avg_withdrawal),
    avgNetRevenue: coerceNumber(raw?.avg_net_revenue),
  };
};

/**
 * The BE returns the valid state codes alongside the data as
 * `meta.filters: [{ key: "state", options: [...] }]`. We pick the entry
 * whose `key === "state"` if present, otherwise default to the first
 * filter (matching the BE guidance — "either response" exposes the same
 * meta on `meta.filters[0]`).
 *
 * v1 ships options as a flat `string[]`; we still tolerate the
 * `{ value, label }` object shape in case the BE ever upgrades, and we
 * always pass through the special `"all"` sentinel (BE-recommended way
 * to say "no filter").
 */
const extractStateOptions = (
  meta: { filters?: RawFilterDef[] | null } | null | undefined,
): StateFilterOption[] => {
  const filters = Array.isArray(meta?.filters) ? meta!.filters! : [];
  const stateFilter =
    filters.find((f) => f?.key === "state") ?? filters[0] ?? null;
  const options = Array.isArray(stateFilter?.options)
    ? stateFilter!.options!
    : [];

  return options
    .map((option) => {
      if (typeof option === "string") {
        const value = option.trim();
        return value ? { value, label: value } : null;
      }
      if (option && typeof option === "object") {
        const value = (option.value ?? "").toString().trim();
        if (!value) return null;
        const label = (option.label ?? value).toString().trim();
        return { value, label };
      }
      return null;
    })
    .filter((option): option is StateFilterOption => Boolean(option));
};

/**
 * Reads the BE envelope:
 *   {
 *     data: { items: [...rows], total, page, size, pages },
 *     meta: { filters: [{ key: "state", options: [...] }] }
 *   }
 *
 * Safe to call with `null` or partial rows — anything we can't read
 * collapses to a zero value so the table still renders.
 */
export const parseAcquisitionSourcesResponse = (
  body: unknown,
): AcquisitionSourcesPayload => {
  const envelope = body as
    | {
        data?:
          | {
              items?: unknown;
              total?: number | string | null;
              page?: number | string | null;
              size?: number | string | null;
              pages?: number | string | null;
            }
          | null;
        meta?: { filters?: RawFilterDef[] | null } | null;
      }
    | null
    | undefined;
  const items = Array.isArray(envelope?.data?.items)
    ? (envelope!.data!.items as unknown[])
    : [];

  const rows = items
    .filter((row): row is RawAcquisitionSourceRow => Boolean(row))
    .map((row, index) => mapRow(row, index));

  return {
    rows,
    stateOptions: extractStateOptions(envelope?.meta),
    pagination: {
      total: coerceNumber(envelope?.data?.total),
      // BE pagination is 1-indexed; fall back to page 1 when missing so
      // controlled antd `current` never receives 0/NaN.
      page: coerceNumber(envelope?.data?.page) || 1,
      size: coerceNumber(envelope?.data?.size),
      pages: coerceNumber(envelope?.data?.pages),
    },
  };
};

export const fetchAcquisitionSources = async (
  filters: AcquisitionSourcesFilters,
): Promise<AcquisitionSourcesPayload> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    API_ROUTES.PROMOTIONS_ACQUISITION_SOURCES,
    { params: buildParams(filters) },
  );
  return parseAcquisitionSourcesResponse(response?.data);
};

export const useGetAcquisitionSources = (filters: AcquisitionSourcesFilters) => {
  const { start_date, end_date, state, page, size } = filters;

  return useQuery<AcquisitionSourcesPayload, Error>({
    queryKey: [
      "PROMOTIONS_ACQUISITION_SOURCES",
      start_date,
      end_date,
      state ?? "",
      page ?? null,
      size ?? null,
    ],
    queryFn: () => fetchAcquisitionSources(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
